/**
 * Primary section "Mietanpassung" (index 6) on the tenant unit contract editor.
 * Loads and saves via Lux API `GET|PUT /api/contracts/{contract_id}/rental_adaptation`.
 * Accordion coordination with other injected sections uses the same classes as Mietminderung.
 * @param {{ unitContractId: number | null }} ctx
 */
const injectRentalAdaptation = (ctx) => {
  const SECTION_ID = 'RentalAdaptation'
  if (document.getElementById(SECTION_ID)) return

  const unitContractId = ctx?.unitContractId ?? null
  if (unitContractId == null) return

  const tenantEditor = document.querySelector('.TenantContractEditor')
  if (!tenantEditor) return

  const pageContent =
    tenantEditor.querySelector('.PageContent[data-testid="page-content-undefined"]') ||
    tenantEditor.querySelector('.PageContent')
  if (!pageContent) return

  const root = document.createElement('div')
  root.className = 'PrimarySectionWrapper RentalAdaptation'
  root.id = SECTION_ID
  root.setAttribute('data-testid', 'primary-section-wrapper-RentalAdaptation')

  const wrapperContent = document.createElement('div')
  wrapperContent.className = 'section-wrapper-content'

  const header = document.createElement('div')
  header.className = 'section-header'
  header.setAttribute('role', 'button')
  header.setAttribute('tabindex', '0')

  const titleWrapper = document.createElement('div')
  titleWrapper.className = 'section-title-wrapper'

  const numberWrap = document.createElement('div')
  numberWrap.className = 'section-number-wrapper'
  const numberEl = document.createElement('div')
  numberEl.className = 'section-number'
  numberEl.textContent = '6'
  numberWrap.appendChild(numberEl)

  const titleEl = document.createElement('div')
  titleEl.className = 'section-title'
  titleEl.textContent = 'Mietanpassung'

  titleWrapper.appendChild(numberWrap)
  titleWrapper.appendChild(titleEl)
  header.appendChild(titleWrapper)

  const sectionBody = document.createElement('div')
  sectionBody.className = 'section-content section-content-with-padding'
  sectionBody.style.display = 'none'

  const validationError = document.createElement('div')
  validationError.setAttribute('role', 'alert')
  validationError.className = 'RentalAdaptation-validation-error'
  validationError.style.cssText =
    'display:none;margin-bottom:12px;padding:8px 12px;border-radius:6px;background:#fff2f0;border:1px solid #ffccc7;color:#cf1322;font-size:14px;line-height:1.5;'

  const COLUMN_STYLE = 'min-width: 25rem;'

  const mkEmptyColumn = () => {
    const col = document.createElement('div')
    col.className = 'sub-section-content-column'
    col.style.minWidth = '25rem'
    return col
  }

  /**
   * One cell: sub-section-content-column > wrapperClass > ant-row ant-form-item (label | control)
   * @param {string} wrapperClass e.g. 'TextInput ' or 'SelectInput '
   * @param {string} labelText
   * @param {HTMLElement} controlEl
   */
  const mkHostFormItem = (wrapperClass, labelText, controlEl) => {
    const col = document.createElement('div')
    col.className = 'sub-section-content-column'
    col.style.cssText = COLUMN_STYLE

    const wrap = document.createElement('div')
    wrap.className = wrapperClass

    const row = document.createElement('div')
    row.className = 'ant-row ant-form-item'
    row.style.rowGap = '0'

    const labelCol = document.createElement('div')
    labelCol.className = 'ant-col ant-form-item-label'
    const label = document.createElement('label')
    label.className = ''
    const spanOuter = document.createElement('span')
    const spanInner = document.createElement('span')
    spanInner.textContent = labelText
    spanOuter.appendChild(spanInner)
    label.appendChild(spanOuter)
    labelCol.appendChild(label)

    const controlCol = document.createElement('div')
    controlCol.className = 'ant-col ant-form-item-control'
    const c1 = document.createElement('div')
    c1.className = 'ant-form-item-control-input'
    const c2 = document.createElement('div')
    c2.className = 'ant-form-item-control-input-content'
    if (controlEl) c2.appendChild(controlEl)
    c1.appendChild(c2)
    controlCol.appendChild(c1)

    row.appendChild(labelCol)
    row.appendChild(controlCol)
    wrap.appendChild(row)
    col.appendChild(wrap)
    return col
  }

  const appendTwoColRow = (container, leftCol, rightCol) => {
    const row = document.createElement('div')
    row.className = 'sub-section-content-row'
    row.appendChild(leftCol)
    row.appendChild(rightCol || mkEmptyColumn())
    container.appendChild(row)
  }

  const mkSelect = (options, withEmpty) => {
    const sel = document.createElement('select')
    sel.className = 'ant-input'
    sel.style.cssText =
      'width:100%;min-height:40px;padding:6px 11px;border-radius:6px;border:1px solid #d9d9d9;background:#fff;'
    if (withEmpty) {
      const o0 = document.createElement('option')
      o0.value = ''
      o0.textContent = '—'
      sel.appendChild(o0)
    }
    options.forEach(({ value, label }) => {
      const o = document.createElement('option')
      o.value = value
      o.textContent = label
      sel.appendChild(o)
    })
    return sel
  }

  const mkNumberInputInner = (attrs) => {
    const wrap = document.createElement('div')
    wrap.className = 'ant-input-number ant-input-number-in-form-item InputNumber Input InputNumber-align-right'
    const inner = document.createElement('div')
    inner.className = 'ant-input-number-input-wrap'
    const input = document.createElement('input')
    input.autocomplete = 'off'
    input.className = 'ant-input-number-input'
    input.style.textAlign = 'right'
    if (attrs) {
      if (attrs.type) input.type = attrs.type
      if (attrs.placeholder != null) input.placeholder = attrs.placeholder
      if (attrs.min != null) input.min = String(attrs.min)
      if (attrs.step != null) input.step = String(attrs.step)
      if (attrs.role) input.setAttribute('role', attrs.role)
      if (attrs.inputmode) input.setAttribute('inputmode', attrs.inputmode)
    }
    inner.appendChild(input)
    if (attrs && attrs.suffix) {
      const suf = document.createElement('span')
      suf.className = 'suffix'
      suf.textContent = attrs.suffix
      inner.appendChild(suf)
    }
    wrap.appendChild(inner)
    return { wrap, input }
  }

  const subMain = document.createElement('div')
  subMain.className = 'sub-section-content'

  const openEndSelect = mkSelect(
    [
      { value: 'ja', label: 'Ja' },
      { value: 'nein', label: 'Nein' },
    ],
    true
  )

  const noticeSelect = mkSelect(
    [
      { value: '3', label: '3 Monate' },
      { value: '6', label: '6 Monate' },
      { value: '9', label: '9 Monate' },
      { value: '12', label: '12 Monate' },
    ],
    true
  )

  const kuendigungsDate = datePicker('rentalAdaptation-kuendigungsdatum')
  const renewalText = document.createElement('input')
  renewalText.type = 'text'
  renewalText.className = 'ant-input Input'
  renewalText.setAttribute('autocomplete', 'off')
  renewalText.style.cssText = 'width:100%;'

  const mietanpassungSelect = mkSelect(
    [
      { value: 'vergleichsmiete', label: 'Vergleichsmiete' },
      { value: 'indexmiete', label: 'Indexmiete' },
      { value: 'staffelmiete', label: 'Staffelmiete' },
      { value: 'keine', label: 'Keine' },
    ],
    true
  )

  appendTwoColRow(
    subMain,
    mkHostFormItem('SelectInput ', 'Kündigungsfrist', openEndSelect),
    mkHostFormItem('SelectInput ', 'Kündigungszeitraum', noticeSelect)
  )
  appendTwoColRow(
    subMain,
    mkHostFormItem('DateInput ', 'Kündigungsdatum', kuendigungsDate.wrapper),
    mkHostFormItem('TextInput ', 'Vertragsverlängerungsoption', renewalText)
  )
  appendTwoColRow(
    subMain,
    mkHostFormItem('SelectInput ', 'Mietanpassung', mietanpassungSelect),
    mkEmptyColumn()
  )

  const indexFromDate = datePicker('rentalAdaptation-index-from')
  const indexValueInput = document.createElement('input')
  indexValueInput.type = 'text'
  indexValueInput.className = 'ant-input Input'
  indexValueInput.setAttribute('autocomplete', 'off')
  indexValueInput.placeholder = 'Indexstand'
  indexValueInput.style.cssText = 'width:100%;'

  const indexBlock = document.createElement('div')
  indexBlock.className = 'RentalAdaptation-index-block'
  indexBlock.style.cssText =
    'display:none;margin-top:8px;padding:16px;border-radius:8px;border:1px solid #f0f0f0;background:#fafafa;'

  const indexSub = document.createElement('div')
  indexSub.className = 'sub-section-content'

  const monthChoices = [
    { value: '6', label: '6 Monate' },
    { value: '12', label: '12 Monate' },
    { value: '24', label: '24 Monate' },
  ]
  const autoRenewSelect = mkSelect(monthChoices, true)

  const thresholdField = mkNumberInputInner({
    type: 'text',
    inputmode: 'decimal',
    placeholder: '0',
    suffix: '%',
  })

  const extensionDeadlineSelect = mkSelect(monthChoices, true)

  const optionsField = mkNumberInputInner({
    type: 'number',
    placeholder: '0',
    min: 0,
    step: 1,
    role: 'spinbutton',
  })

  const yearsField = mkNumberInputInner({
    type: 'number',
    placeholder: '0',
    min: 0,
    step: 1,
    role: 'spinbutton',
  })

  appendTwoColRow(
    indexSub,
    mkHostFormItem('DateInput ', 'Index', indexFromDate.wrapper),
    mkHostFormItem('TextInput ', 'Indexstand', indexValueInput)
  )
  indexBlock.appendChild(indexSub)
  subMain.appendChild(indexBlock)

  appendTwoColRow(
    subMain,
    mkHostFormItem('SelectInput ', 'Automatische Verlängerung', autoRenewSelect),
    mkHostFormItem('NumberInput ', 'Indexhürde / Schwellenwert', thresholdField.wrap)
  )
  appendTwoColRow(
    subMain,
    mkHostFormItem(
      'SelectInput ',
      'Frist zur Ausübung vor Mietende',
      extensionDeadlineSelect
    ),
    mkHostFormItem('NumberInput ', 'Anzahl Optionen', optionsField.wrap)
  )
  appendTwoColRow(
    subMain,
    mkHostFormItem('NumberInput ', 'Verlängerung (Jahre)', yearsField.wrap),
    mkEmptyColumn()
  )

  const formRoot = document.createElement('div')
  formRoot.className = 'RentalAdaptation-form'
  formRoot.appendChild(subMain)

  sectionBody.appendChild(validationError)
  sectionBody.appendChild(formRoot)

  const footer = document.createElement('div')
  footer.className = 'section-footer'
  footer.style.display = 'none'

  const saveBtn = document.createElement('button')
  saveBtn.type = 'button'
  saveBtn.className = 'ant-btn ant-btn-primary Button undefined'
  const saveSpan = document.createElement('span')
  saveSpan.textContent = 'Speichern'
  saveBtn.appendChild(saveSpan)
  footer.appendChild(saveBtn)

  let isFetching = false
  let isSaving = false

  const syncFormInteractivity = () => {
    const locked = isFetching || isSaving

    openEndSelect.disabled = locked
    noticeSelect.disabled = locked
    renewalText.disabled = locked
    mietanpassungSelect.disabled = locked
    indexValueInput.disabled = locked
    extensionDeadlineSelect.disabled = locked
    thresholdField.input.disabled = locked
    optionsField.input.disabled = locked
    yearsField.input.disabled = locked

    ;[kuendigungsDate, indexFromDate].forEach((inp) => {
      inp.disabled = locked
      const w = inp.wrapper
      if (w) {
        w.style.pointerEvents = locked ? 'none' : ''
        w.setAttribute('aria-busy', locked ? 'true' : 'false')
      }
    })

    if (locked) {
      autoRenewSelect.disabled = true
      autoRenewSelect.style.opacity = '0.5'
      autoRenewSelect.style.cursor = 'not-allowed'
      autoRenewSelect.title = ''
    } else {
      syncAutoRenewDisabledState()
    }

    saveBtn.disabled = locked
    if (isFetching) {
      saveSpan.textContent = 'Lädt...'
    } else if (isSaving) {
      saveSpan.textContent = 'Wird gespeichert...'
    } else {
      saveSpan.textContent = 'Speichern'
    }
  }

  const authToken = () => {
    try {
      const raw = localStorage.getItem('auth')
      return raw ? JSON.parse(raw).token : null
    } catch {
      return null
    }
  }

  const apiRoot = typeof baseApiUrl !== 'undefined' && baseApiUrl ? baseApiUrl : ''

  const showApiError = (message) => {
    if (!message) {
      validationError.style.display = 'none'
      validationError.textContent = ''
      return
    }
    validationError.style.display = 'block'
    validationError.textContent = message
  }

  const ymdFromRow = (v) => {
    if (v == null || v === '') return ''
    const s = String(v)
    return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : s
  }

  const applyServerRow = (row) => {
    if (row.openEndContract === true) openEndSelect.value = 'ja'
    else if (row.openEndContract === false) openEndSelect.value = 'nein'
    else openEndSelect.value = ''

    noticeSelect.value =
      row.advanceNoticeMonths != null && row.advanceNoticeMonths !== ''
        ? String(row.advanceNoticeMonths)
        : ''

    if (typeof kuendigungsDate.setValue === 'function') {
      kuendigungsDate.setValue(ymdFromRow(row.latestTerminationDate))
    }
    renewalText.value = row.renewalOptionText != null ? String(row.renewalOptionText) : ''
    mietanpassungSelect.value = row.mietanpassung != null ? String(row.mietanpassung) : ''

    if (typeof indexFromDate.setValue === 'function') {
      indexFromDate.setValue(ymdFromRow(row.indexEffectiveDate))
    }
    indexValueInput.value = row.indexValueText != null ? String(row.indexValueText) : ''

    autoRenewSelect.value =
      row.automaticRenewalMonths != null && row.automaticRenewalMonths !== ''
        ? String(row.automaticRenewalMonths)
        : ''
    thresholdField.input.value =
      row.indexThresholdPercent != null ? String(row.indexThresholdPercent) : ''
    extensionDeadlineSelect.value =
      row.exerciseDeadlineMonths != null && row.exerciseDeadlineMonths !== ''
        ? String(row.exerciseDeadlineMonths)
        : ''
    optionsField.input.value =
      row.optionCount != null && row.optionCount !== '' ? String(row.optionCount) : ''
    yearsField.input.value =
      row.extensionYears != null && row.extensionYears !== '' ? String(row.extensionYears) : ''

    syncIndexBlockVisibility()
    syncAutoRenewDisabledState()
  }

  const loadFromApi = async () => {
    const token = authToken()
    if (!token) {
      showApiError('Nicht angemeldet.')
      return
    }
    isFetching = true
    syncFormInteractivity()
    try {
      const res = await fetch(`${apiRoot}/api/contracts/${unitContractId}/rental_adaptation`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        showApiError(data.error || 'Daten konnten nicht geladen werden.')
        return
      }
      const row = data.rental_adaptation
      if (row && typeof row === 'object') {
        applyServerRow(row)
      }
      showApiError('')
    } catch (e) {
      console.error('rental_adaptation load', e)
      showApiError('Daten konnten nicht geladen werden.')
    } finally {
      isFetching = false
      syncFormInteractivity()
    }
  }

  saveBtn.addEventListener('click', async () => {
    showApiError('')
    if (!mietanpassungSelect.value) {
      showApiError('Bitte „Mietanpassung“ auswählen.')
      return
    }
    const token = authToken()
    if (!token) {
      alert('Nicht angemeldet.')
      return
    }
    const parseSelInt = (sel) => {
      const v = sel.value
      if (!v) return null
      const n = parseInt(v, 10)
      return Number.isFinite(n) ? n : null
    }
    const parseInputInt = (inp) => {
      const v = String(inp.value || '').trim()
      if (v === '') return null
      const n = parseInt(v, 10)
      return Number.isFinite(n) && n >= 0 ? n : null
    }

    const openRaw = openEndSelect.value
    let openEndContract = null
    if (openRaw === 'ja') openEndContract = true
    else if (openRaw === 'nein') openEndContract = false

    const latestRaw =
      typeof kuendigungsDate.getValue === 'function' ? kuendigungsDate.getValue().trim() : ''
    const indexRaw =
      typeof indexFromDate.getValue === 'function' ? indexFromDate.getValue().trim() : ''

    const body = {
      openEndContract,
      advanceNoticeMonths: parseSelInt(noticeSelect),
      latestTerminationDate: latestRaw || null,
      renewalOptionText: renewalText.value.trim() || null,
      mietanpassung: mietanpassungSelect.value,
      indexEffectiveDate: indexRaw || null,
      indexValueText: indexValueInput.value.trim() || null,
      automaticRenewalMonths: parseSelInt(autoRenewSelect),
      indexThresholdPercent: thresholdField.input.value.trim() || null,
      exerciseDeadlineMonths: parseSelInt(extensionDeadlineSelect),
      optionCount: parseInputInt(optionsField.input),
      extensionYears: parseInputInt(yearsField.input),
    }

    isSaving = true
    syncFormInteractivity()
    try {
      const res = await fetch(`${apiRoot}/api/contracts/${unitContractId}/rental_adaptation`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      })
      const payload = await res.json().catch(() => ({}))
      if (!res.ok) {
        showApiError(payload.error || 'Speichern fehlgeschlagen.')
        return
      }
      if (payload.rental_adaptation && typeof payload.rental_adaptation === 'object') {
        applyServerRow(payload.rental_adaptation)
      }
      showApiError('')
    } catch (e) {
      console.error('rental_adaptation save', e)
      showApiError('Speichern fehlgeschlagen.')
    } finally {
      isSaving = false
      syncFormInteractivity()
    }
  })

  const closeSectionDom = (wc) => {
    wc.classList.remove('section-wrapper-open')
    const content = wc.querySelector(':scope > .section-content')
    if (content) content.style.display = 'none'
    const foot = wc.querySelector(':scope > .section-footer')
    if (foot) foot.style.display = 'none'
  }

  const closeOtherPrimarySections = (exceptWrapper) => {
    pageContent.querySelectorAll(':scope > .PrimarySectionWrapper > .section-wrapper-content').forEach((wc) => {
      if (wc === exceptWrapper) return
      if (!wc.classList.contains('section-wrapper-open')) return
      closeSectionDom(wc)
    })
  }

  let isOpen = false
  let didLoadRentalAdaptation = false

  const syncIndexBlockVisibility = () => {
    const v = mietanpassungSelect.value
    const show = v === 'indexmiete'
    indexBlock.style.display = show ? 'block' : 'none'
    if (show) syncAutoRenewDisabledState()
  }

  const syncAutoRenewDisabledState = () => {
    if (isFetching || isSaving) return
    const openEndJa = openEndSelect.value === 'ja'
    const grey = openEndJa
    autoRenewSelect.disabled = grey
    autoRenewSelect.style.opacity = grey ? '0.5' : ''
    autoRenewSelect.style.cursor = grey ? 'not-allowed' : ''
    autoRenewSelect.title = grey
      ? 'Bei unbefristetem Vertrag nicht relevant'
      : ''
  }

  /** Same rules as API: only Index / Indexstand when Mietanpassung is Indexmiete; open-end Ja clears Automatische Verlängerung. */
  const clearIndexMieteOnlyFields = () => {
    if (typeof indexFromDate.setValue === 'function') indexFromDate.setValue('')
    indexValueInput.value = ''
  }

  const onMietanpassungChange = () => {
    if (mietanpassungSelect.value !== 'indexmiete') {
      clearIndexMieteOnlyFields()
    }
    syncIndexBlockVisibility()
  }

  const onOpenEndChange = () => {
    if (openEndSelect.value === 'ja') {
      autoRenewSelect.value = ''
    }
    syncAutoRenewDisabledState()
  }

  mietanpassungSelect.addEventListener('change', onMietanpassungChange)
  openEndSelect.addEventListener('change', onOpenEndChange)

  const applyOpenState = (open) => {
    isOpen = open
    if (open) {
      closeOtherPrimarySections(wrapperContent)
      wrapperContent.classList.add('section-wrapper-open')
      sectionBody.style.display = ''
      footer.style.display = ''
      if (!didLoadRentalAdaptation) {
        didLoadRentalAdaptation = true
        loadFromApi()
      }
    } else {
      wrapperContent.classList.remove('section-wrapper-open')
      sectionBody.style.display = 'none'
      footer.style.display = 'none'
    }
  }

  const toggle = () => applyOpenState(!isOpen)

  header.addEventListener('click', (e) => {
    e.preventDefault()
    toggle()
  })
  header.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      toggle()
    }
  })

  if (!document.getElementById('RentalAdaptation-styles')) {
    const st = document.createElement('style')
    st.id = 'RentalAdaptation-styles'
    st.textContent = `
      #RentalAdaptation .section-content.section-content-with-padding {
        overflow: visible !important;
      }
      #RentalAdaptation .lux-picker-dropdown {
        z-index: 12000;
      }
    `
    document.head.appendChild(st)
  }

  wrapperContent.appendChild(header)
  wrapperContent.appendChild(sectionBody)
  wrapperContent.appendChild(footer)
  root.appendChild(wrapperContent)
  pageContent.appendChild(root)

  syncIndexBlockVisibility()
  syncFormInteractivity()
}
